import { useState, useEffect } from "react"
import { Edit, Globe, Target, Users, Activity, TrendingUp, Calendar, Clock, GripVertical, X, Loader2, Search, Check, Building2 } from "lucide-react"
import { CampaignService } from "@/features/campaigns"
import { OrganizationService } from "@/features/organizations"
import { InsuranceCarriersModal } from "@/features/campaigns/components/InsuranceCarriers"

// Shimmer Effect Components
const ShimmerCard = () => (
  <div className="bg-white border-2 border-gray-200 rounded-xl p-6 animate-pulse">
    <div className="text-center">
      <div className="w-12 h-12 bg-gray-200 rounded-xl mx-auto mb-4"></div>
      <div className="h-6 bg-gray-200 rounded mb-2"></div>
      <div className="h-4 bg-gray-200 rounded mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto"></div>
    </div>
  </div>
)

const ShimmerButton = () => (
  <div className="bg-gradient-to-br from-gray-200 to-gray-300 text-transparent p-6 rounded-xl shadow-lg animate-pulse">
    <div className="text-center">
      <div className="text-2xl font-bold mb-2 bg-gray-300 rounded h-8 w-20 mx-auto"></div>
      <div className="text-xs opacity-90 bg-gray-300 rounded h-4 w-32 mx-auto"></div>
    </div>
  </div>
)

const ShimmerInput = () => (
  <div className="bg-gray-200 rounded-xl h-12 animate-pulse"></div>
)

const ShimmerSection = () => (
  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 border-b border-gray-200 animate-pulse">
    <div className="flex items-center gap-4 mb-8">
      <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
      <div>
        <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-96"></div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
      {[...Array(5)].map((_, i) => (
        <ShimmerCard key={i} />
      ))}
    </div>
  </div>
)

const ShimmerStatusSection = () => (
  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 border-b border-gray-200 animate-pulse">
    <div className="flex items-center gap-4 mb-8">
      <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
      <div>
        <div className="h-6 bg-gray-200 rounded w-40 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-80"></div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {[...Array(5)].map((_, i) => (
        <ShimmerButton key={i} />
      ))}
    </div>
  </div>
)



interface EditCampaignProps {
  campaignId?: string
  campaignName?: string
  onCampaignUpdated?: (updatedCampaign: any) => void
}

interface CampaignSettings {
  name: string
  countries: string[]
  states: string[]
  zipcodes: string[]
  allZipcodes: boolean
  minAge: number | null
  maxAge: number | null
  allowedGenders: string[]
  hiringFreeze: boolean
  minOverrideRate: number | null
  maxOverrideRate: number | null
  salesDays: string[]
  salesTimes: { start: string; end: string; is24Hours: boolean }
  assignedOrgs: any[]
  unassignedOrgs: any[]
  blacklistedOrgs: any[]
}

interface OrganizationAssignment {
  organizationId: string
  organizationName: string
  organizationType: string
  isActive: boolean
}

interface EditModals {
  name: boolean
  countries: boolean
  states: boolean
  zipcodes: boolean
  ageEligibility: boolean
  genderEligibility: boolean
  hiringFreeze: boolean
  overrideRateLimits: boolean
  salesDays: boolean
  salesTimes: boolean
  orgAssignment: boolean
}

interface DraggedItem {
  org: string
  source: string
}

// Country name to ISO code mapping
const COUNTRY_MAPPING: { [key: string]: string } = {
  'United States': 'US',
  'Canada': 'CA',
  'United Kingdom': 'GB',
  'Australia': 'AU',
  'Germany': 'DE',
  'France': 'FR',
  'Spain': 'ES',
  'Italy': 'IT',
  'Netherlands': 'NL',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Denmark': 'DK'
}

// ISO code to country name mapping (reverse)
const ISO_TO_COUNTRY_MAPPING: { [key: string]: string } = {
  'US': 'United States',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'AU': 'Australia',
  'DE': 'Germany',
  'FR': 'France',
  'ES': 'Spain',
  'IT': 'Italy',
  'NL': 'Netherlands',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark'
}

// State name to 2-character code mapping
const STATE_MAPPING: { [key: string]: string } = {
  'Alabama': 'AL',
  'Alaska': 'AK',
  'Arizona': 'AZ',
  'Arkansas': 'AR',
  'California': 'CA',
  'Colorado': 'CO',
  'Connecticut': 'CT',
  'Delaware': 'DE',
  'Florida': 'FL',
  'Georgia': 'GA',
  'Hawaii': 'HI',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Indiana': 'IN',
  'Iowa': 'IA',
  'Kansas': 'KS',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Maine': 'ME',
  'Maryland': 'MD',
  'Massachusetts': 'MA',
  'Michigan': 'MI',
  'Minnesota': 'MN',
  'Mississippi': 'MS',
  'Missouri': 'MO',
  'Montana': 'MT',
  'Nebraska': 'NE',
  'Nevada': 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Ohio': 'OH',
  'Oklahoma': 'OK',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Vermont': 'VT',
  'Virginia': 'VA',
  'Washington': 'WA',
  'West Virginia': 'WV',
  'Wisconsin': 'WI',
  'Wyoming': 'WY',
  'Puerto Rico': 'PR'
}

// State code to name mapping (reverse)
const CODE_TO_STATE_MAPPING: { [key: string]: string } = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming',
  'PR': 'Puerto Rico'
}

export default function EditCampaign({ campaignId, campaignName, onCampaignUpdated }: EditCampaignProps) {
  // Campaign data state
  const [campaign, setCampaign] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  
  // Organization data state
  const [organizations, setOrganizations] = useState<any[]>([])
  const [organizationAssignments, setOrganizationAssignments] = useState<any[]>([])
  const [orgSearchTerm, setOrgSearchTerm] = useState('')
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
  const [isUpdatingOrgAssignment, setIsUpdatingOrgAssignment] = useState(false)
  
  // Edit Campaign Settings State
  const [editModals, setEditModals] = useState<EditModals>({
    name: false,
    countries: false,
    states: false,
    zipcodes: false,
    ageEligibility: false,
    genderEligibility: false,
    hiringFreeze: false,
    overrideRateLimits: false,
    salesDays: false,
    salesTimes: false,
    orgAssignment: false
  })

  // Insurance Carriers Modal State
  const [showInsuranceCarriersModal, setShowInsuranceCarriersModal] = useState(false)

  const [campaignSettings, setCampaignSettings] = useState<CampaignSettings>({
    name: campaignName || '',
    countries: ['United States', 'Canada'],
    states: ['California', 'Texas', 'Florida', 'New York'],
    zipcodes: ['90210', '10001', '33101'],
    allZipcodes: false,
    minAge: null,
    maxAge: null,
    allowedGenders: [],
    hiringFreeze: false,
    minOverrideRate: null,
    maxOverrideRate: null,
    salesDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    salesTimes: { start: '09:00', end: '17:00', is24Hours: false },
    assignedOrgs: [],
    unassignedOrgs: [],
    blacklistedOrgs: []
  })

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null)
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null)
  
  // Loading states for updates
  const [isUpdating, setIsUpdating] = useState(false)
  const [loadingStates, setLoadingStates] = useState<{[key: string]: boolean}>({
    name: false,
    countries: false,
    states: false,
    zipcodes: false,
    minAge: false,
    maxAge: false,
    allowedGenders: false,
    hiringFreeze: false,
    minOverrideRate: false,
    maxOverrideRate: false,
    overrideRateLimits: false,
    salesDays: false,
    salesTimes: false
  })
  
  // Local state for modal inputs (to avoid saving on every keystroke)
  const [localName, setLocalName] = useState('')
  const [localCountries, setLocalCountries] = useState<string[]>([])
  const [localStates, setLocalStates] = useState<string[]>([])
  const [localZipcodes, setLocalZipcodes] = useState<string>('')
  const [localAllZipcodes, setLocalAllZipcodes] = useState<boolean>(false)
  const [localMinAge, setLocalMinAge] = useState<number | null>(null)
  const [localMaxAge, setLocalMaxAge] = useState<number | null>(null)
  const [localAllowedGenders, setLocalAllowedGenders] = useState<string[]>([])
  const [localHiringFreeze, setLocalHiringFreeze] = useState<boolean>(false)
  const [localMinOverrideRate, setLocalMinOverrideRate] = useState<number | null>(null)
  const [localMaxOverrideRate, setLocalMaxOverrideRate] = useState<number | null>(null)
  const [localSalesDays, setLocalSalesDays] = useState<string[]>([])
  const [localSalesTimes, setLocalSalesTimes] = useState<{start: string, end: string, is24Hours: boolean}>({start: '09:00', end: '17:00', is24Hours: false})

  // Fetch campaign data on mount or when campaignId changes
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!campaignId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await CampaignService.getCampaignById(campaignId)
        
        if (response.data) {
          setCampaign(response.data)
          // Extract sales schedule from settings
          const salesSchedule = response.data.settings?.salesSchedule || {}
          
          // Update campaign settings with fresh data
          const data = response.data
          setCampaignSettings(prev => ({
            ...prev,
            name: data.name || '',
            countries: data.allowedCountries || [],
            states: data.allowedStates || [],
            zipcodes: (data as any).allowedZipcodes || [],
            allZipcodes: (data as any).allZipcodes || false,
            minAge: (data as any).minAge || null,
            maxAge: (data as any).maxAge || null,
            allowedGenders: (data as any).allowedGenders || [],
            hiringFreeze: data.hiringFreeze || false,
            minOverrideRate: (data as any).minOverrideRate || null,
            maxOverrideRate: (data as any).maxOverrideRate || null,
            salesDays: salesSchedule.salesDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            salesTimes: salesSchedule.salesTimes || { start: '09:00', end: '17:00', is24Hours: false }
          }))
          
          // Notify parent component of updated campaign data
          if (onCampaignUpdated) {
            onCampaignUpdated(response.data)
          }
        }
      } catch (error) {
        console.error('Error fetching campaign data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCampaignData()
  }, [campaignId])

  // Fetch organizations and assignments when campaignId changes
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!campaignId) return

      try {
        setIsLoadingOrgs(true)
        
        // Fetch all organizations and current assignments in parallel
        const [orgsResponse, assignmentsResponse] = await Promise.all([
          OrganizationService.getOrganizations(),
          CampaignService.getCampaignOrganizationAssignments(campaignId)
        ])
        
        if (orgsResponse.data) {
          setOrganizations(orgsResponse.data)
        }
        
        if (assignmentsResponse.data) {
          setOrganizationAssignments(assignmentsResponse.data)
          
        // Update campaign settings with real assignment data
        const assignedOrgs = assignmentsResponse.data?.filter(assignment => assignment.isActive) || []
        const unassignedOrgs = orgsResponse.data?.filter(org => 
          !assignmentsResponse.data?.some(assignment => 
            assignment.organizationId === org.id && assignment.isActive
          )
        ) || []
          
          setCampaignSettings(prev => ({
            ...prev,
            assignedOrgs: assignedOrgs.map(assignment => ({
              id: assignment.organizationId,
              name: assignment.organizationName || assignment.organization?.name,
              type: assignment.organizationType || assignment.organization?.type,
              isActive: assignment.isActive
            })),
            unassignedOrgs: unassignedOrgs.map(org => ({
              id: org.id,
              name: org.name,
              type: org.type,
              isActive: org.isActive
            })),
            blacklistedOrgs: [] // Initialize empty - would need separate API call for blacklisted orgs
          }))
        }
      } catch (error) {
        console.error('Error fetching organization data:', error)
      } finally {
        setIsLoadingOrgs(false)
      }
    }

    fetchOrganizationData()
  }, [campaignId])

  // Filter organizations based on search term
  const filterOrganizations = (orgs: any[]) => {
    if (!orgSearchTerm) return orgs
    return orgs.filter(org => 
      org.name.toLowerCase().includes(orgSearchTerm.toLowerCase()) ||
      org.type.toLowerCase().includes(orgSearchTerm.toLowerCase())
    )
  }

  // Modal functions
  const openModal = (modalName: keyof EditModals) => {
    setEditModals(prev => ({ ...prev, [modalName]: true }))
    
    // Initialize local state when opening modals
    if (modalName === 'name') {
      setLocalName(campaignSettings.name)
    } else if (modalName === 'countries') {
      // Convert ISO codes back to country names for display
      const countryNames = campaignSettings.countries.map((isoCode: string) => ISO_TO_COUNTRY_MAPPING[isoCode] || isoCode)
      setLocalCountries(countryNames)
    } else if (modalName === 'states') {
      // Convert state codes back to state names for display
      const stateNames = campaignSettings.states.map((stateCode: string) => CODE_TO_STATE_MAPPING[stateCode] || stateCode)
      setLocalStates(stateNames)
    } else if (modalName === 'zipcodes') {
      setLocalZipcodes(campaignSettings.zipcodes.join(', '))
      setLocalAllZipcodes(campaignSettings.allZipcodes)
    } else if (modalName === 'ageEligibility') {
      setLocalMinAge(campaignSettings.minAge)
      setLocalMaxAge(campaignSettings.maxAge)
    } else if (modalName === 'genderEligibility') {
      setLocalAllowedGenders(campaignSettings.allowedGenders)
    } else if (modalName === 'hiringFreeze') {
      setLocalHiringFreeze(campaignSettings.hiringFreeze)
    } else if (modalName === 'overrideRateLimits') {
      setLocalMinOverrideRate(campaignSettings.minOverrideRate)
      setLocalMaxOverrideRate(campaignSettings.maxOverrideRate)
    } else if (modalName === 'salesDays') {
      setLocalSalesDays(campaignSettings.salesDays)
    } else if (modalName === 'salesTimes') {
      setLocalSalesTimes(campaignSettings.salesTimes)
    }
  }

  const closeModal = (modalName: keyof EditModals) => {
    setEditModals(prev => ({ ...prev, [modalName]: false }))
  }

  const updateCampaignStatus = async (newStatus: string) => {
    if (!campaignId) {
      alert("Error: No campaign selected")
      return
    }

    setIsUpdating(true)
    
    try {
      const response = await CampaignService.updateCampaignStatus(campaignId, newStatus as any)
      
      if (response.data) {
        setCampaign(response.data) // Update the campaign data with fresh response
        
        // Notify parent component of updated campaign data
        if (onCampaignUpdated) {
          onCampaignUpdated(response.data)
        }
        
        alert(`Campaign status updated to ${newStatus} successfully!`)
      } else {
        alert(`Failed to update campaign status: ${response.error || 'Unknown error'}`)
      }
    } catch (error) {
      alert(`Error updating campaign status: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsUpdating(false)
    }
  }

  const updateSetting = async (setting: keyof CampaignSettings | 'overrideRateLimits', value: any, suppressAlert: boolean = false) => {
    if (!campaignId) {
      alert("Error: No campaign selected")
      return
    }

    setLoadingStates(prev => ({ ...prev, [setting]: true }))

    try {
      let updateData: any = {}
      
      switch (setting) {
        case 'name':
          updateData = { displayName: value }
          break
        case 'countries':
          updateData = { allowedCountries: value }
          break
        case 'states':
          updateData = { allowedStates: value }
          break
        case 'zipcodes':
          updateData = { allowedZipcodes: value.zipcodes, allZipcodes: value.allZipcodes }
          break
        case 'minAge':
        case 'maxAge':
          updateData = { [setting]: value }
          break
        case 'allowedGenders':
          updateData = { allowedGenders: value }
          break
        case 'hiringFreeze':
          updateData = { hiringFreeze: value }
          break
        case 'overrideRateLimits':
          updateData = {
            minOverrideRate: value.minOverrideRate,
            maxOverrideRate: value.maxOverrideRate
          }
          break
        case 'salesDays':
        case 'salesTimes':
          // Update settings with sales schedule data
          const currentSettings = campaign?.settings || {}
          updateData = { 
            settings: {
              ...currentSettings,
              salesSchedule: {
                ...currentSettings.salesSchedule,
                [setting]: value
              }
            }
          }
          break
        default:
          // For other settings that don't have API endpoints yet, just update local state
          setCampaignSettings(prev => ({ ...prev, [setting]: value }))
          setLoadingStates(prev => ({ ...prev, [setting]: false }))
          return
      }

      const response = await CampaignService.updateCampaign(campaignId, updateData as any)
      
      if (response.data) {
        setCampaignSettings(prev => ({ ...prev, [setting]: value }))
        setCampaign(response.data) // Update the campaign data with fresh response
        
        // Notify parent component of updated campaign data
        if (onCampaignUpdated) {
          onCampaignUpdated(response.data)
        }
        
        if (!suppressAlert) {
          alert(`${setting} updated successfully!`)
        }
      } else {
        alert(`Failed to update ${setting}: ${response.error || 'Unknown error'}`)
      }
    } catch (error) {
      alert(`Error updating ${setting}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoadingStates(prev => ({ ...prev, [setting]: false }))
    }
  }

  // Drag and drop functions
  const handleDragStart = (e: React.DragEvent, org: string, source: string) => {
    setDraggedItem({ org, source })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, target: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverTarget(target)
  }

  const handleDragLeave = () => {
    setDragOverTarget(null)
  }

  const handleDrop = async (e: React.DragEvent, target: string) => {
    e.preventDefault()
    setDragOverTarget(null)

    if (!draggedItem || !campaignId) return

    const { org, source } = draggedItem

    // Don't do anything if dropping in the same place
    if (source === target) {
      setDraggedItem(null)
      return
    }

    setIsUpdatingOrgAssignment(true)

    try {
      // Handle different drag and drop scenarios
      if (source === 'unassigned' && target === 'assigned') {
        // Assign organization to campaign (always inherit to children)
        const result = await CampaignService.assignCampaignToOrganization(
          campaignId, 
          typeof org === 'string' ? org : (org as any).id,
          true  // Always inherit to child organizations
        )
        
        // Refetch organization assignments to show all inherited children
        const [orgsResponse, assignmentsResponse] = await Promise.all([
          OrganizationService.getOrganizations(),
          CampaignService.getCampaignOrganizationAssignments(campaignId)
        ])
        
        if (orgsResponse.data && assignmentsResponse.data) {
          const assignedOrgs = assignmentsResponse.data?.filter(assignment => assignment.isActive) || []
          const unassignedOrgs = orgsResponse.data?.filter(org => 
            !assignmentsResponse.data?.some(assignment => 
              assignment.organizationId === org.id && assignment.isActive
            )
          ) || []
          
          setCampaignSettings(prev => ({
            ...prev,
            assignedOrgs: assignedOrgs.map(assignment => ({
              id: assignment.organizationId,
              name: assignment.organizationName || assignment.organization?.name,
              type: assignment.organizationType || assignment.organization?.type,
              isActive: assignment.isActive
            })),
            unassignedOrgs: unassignedOrgs.map(org => ({
              id: org.id,
              name: org.name,
              type: org.type,
              isActive: org.isActive
            }))
          }))
        }
        
        const orgName = typeof org === 'string' ? org : (org as any).name
        const message = result.data?.assignedCount > 1
          ? `${orgName} and ${result.data.assignedCount - 1} child organizations have been assigned to the campaign successfully!`
          : `${orgName} has been assigned to the campaign successfully!`
        alert(message)
        
      } else if (source === 'assigned' && target === 'unassigned') {
        // Remove organization from campaign
        await CampaignService.removeCampaignFromOrganization(campaignId, typeof org === 'string' ? org : (org as any).id)
        
        // Update local state
        setCampaignSettings(prev => ({
          ...prev,
          assignedOrgs: prev.assignedOrgs.filter((o: any) => (typeof o === 'string' ? o : o.id) !== (typeof org === 'string' ? org : (org as any).id)),
          unassignedOrgs: [...prev.unassignedOrgs, org]
        }))
        
        alert(`${typeof org === 'string' ? org : (org as any).name} has been removed from the campaign successfully!`)
        
      } else if (source === 'blacklisted' && target === 'assigned') {
        // Move from blacklist to assigned - assign to campaign (always inherit to children)
        const result = await CampaignService.assignCampaignToOrganization(
          campaignId, 
          typeof org === 'string' ? org : (org as any).id,
          true  // Always inherit to child organizations
        )
        
        // Refetch organization assignments to show all inherited children
        const [orgsResponse, assignmentsResponse] = await Promise.all([
          OrganizationService.getOrganizations(),
          CampaignService.getCampaignOrganizationAssignments(campaignId)
        ])
        
        if (orgsResponse.data && assignmentsResponse.data) {
          const assignedOrgs = assignmentsResponse.data?.filter(assignment => assignment.isActive) || []
          const unassignedOrgs = orgsResponse.data?.filter(org => 
            !assignmentsResponse.data?.some(assignment => 
              assignment.organizationId === org.id && assignment.isActive
            )
          ) || []
          
          setCampaignSettings(prev => ({
            ...prev,
            assignedOrgs: assignedOrgs.map(assignment => ({
              id: assignment.organizationId,
              name: assignment.organizationName || assignment.organization?.name,
              type: assignment.organizationType || assignment.organization?.type,
              isActive: assignment.isActive
            })),
            unassignedOrgs: unassignedOrgs.map(org => ({
              id: org.id,
              name: org.name,
              type: org.type,
              isActive: org.isActive
            })),
            blacklistedOrgs: prev.blacklistedOrgs.filter((o: any) => (typeof o === 'string' ? o : o.id) !== (typeof org === 'string' ? org : (org as any).id))
          }))
        }
        
        const orgName = typeof org === 'string' ? org : (org as any).name
        const message = result.data?.assignedCount > 1
          ? `${orgName} and ${result.data.assignedCount - 1} child organizations have been removed from blacklist and assigned to the campaign!`
          : `${orgName} has been removed from blacklist and assigned to the campaign!`
        alert(message)
        
      } else if (source === 'blacklisted' && target === 'unassigned') {
        // Move from blacklist to unassigned - just remove from blacklist
        // No API call needed since blacklist is only frontend state
        
        // Update local state
        setCampaignSettings(prev => ({
          ...prev,
          blacklistedOrgs: prev.blacklistedOrgs.filter((o: any) => (typeof o === 'string' ? o : o.id) !== (typeof org === 'string' ? org : (org as any).id)),
          unassignedOrgs: [...prev.unassignedOrgs, org]
        }))
        
        alert(`${typeof org === 'string' ? org : (org as any).name} has been removed from blacklist!`)
        
      } else if (target === 'blacklisted') {
        // Remove from campaign and add to blacklist
        if (source === 'assigned') {
          await CampaignService.removeCampaignFromOrganization(campaignId, typeof org === 'string' ? org : (org as any).id)
        }
        
        // Update local state
        const sourceKey = `${source}Orgs` as keyof CampaignSettings
        setCampaignSettings(prev => ({
          ...prev,
          [sourceKey]: (prev[sourceKey] as any[]).filter((o: any) => (typeof o === 'string' ? o : o.id) !== (typeof org === 'string' ? org : (org as any).id)),
          blacklistedOrgs: [...prev.blacklistedOrgs, org]
        }))
        
        alert(`${typeof org === 'string' ? org : (org as any).name} has been blacklisted from the campaign!`)
      }
      
    } catch (error) {
      console.error('Error updating organization assignment:', error)
      alert(`Failed to update organization assignment: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsUpdatingOrgAssignment(false)
      setDraggedItem(null)
    }
  }

  // Loading screen
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Edit Campaign Settings Shimmer */}
          <ShimmerSection />
          
          {/* Campaign Status Shimmer */}
          <ShimmerStatusSection />
          
          {/* Campaign Sales Schedule Shimmer */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 border-b border-gray-200 animate-pulse">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-56 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-72"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ShimmerCard />
              <ShimmerCard />
            </div>
          </div>
          
          {/* Campaign ORG Assignment Shimmer */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 animate-pulse">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-52 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-80"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white border-2 border-gray-200 rounded-xl p-6 animate-pulse">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                      <div className="h-5 bg-gray-200 rounded w-32"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="space-y-3 min-h-[120px]">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="bg-gray-100 rounded-lg p-3 animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!campaignId) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8">
          <div className="text-center">
            <div className="text-orange-600 text-lg font-semibold mb-2">No Campaign Selected</div>
            <p className="text-gray-600">Please select a campaign to edit its settings.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">

      {/* Main Settings Container */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">

        {/* Top Row - Edit Campaign Settings */}
        {/* Top Row - Edit Campaign Settings */}
<div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 border-b border-gray-200">
  <div className="flex items-start justify-between gap-4 mb-8">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
        <Edit className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900">Edit Campaign Settings</h2>
        <p className="text-gray-600">Configure basic campaign information and availability</p>
      </div>
    </div>
    <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
      <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
      <span className="text-sm font-semibold text-orange-800">Campaign Configuration</span>
    </div>
  </div>

  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
    {loadingStates.name ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('name')}
        className="group bg-white border-2 border-blue-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Edit className="w-7 h-7 text-blue-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">EDIT HOME PAGE<br />DISPLAY NAME</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Modify campaign name shown on home page</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-blue-600 truncate">Current: {campaignSettings.name}</p>
          </div>
        </div>
      </button>
    )}

    {loadingStates.countries ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('countries')}
        className="group bg-white border-2 border-green-200 rounded-2xl p-6 hover:border-green-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Globe className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">EDIT COUNTRIES<br />AVAILABLE</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Select countries allowed for sales</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-green-600">
              {campaignSettings.countries.map((isoCode: string) => ISO_TO_COUNTRY_MAPPING[isoCode] || isoCode).length} countries selected
            </p>
          </div>
        </div>
      </button>
    )}

    {loadingStates.states ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('states')}
        className="group bg-white border-2 border-purple-200 rounded-2xl p-6 hover:border-purple-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Target className="w-7 h-7 text-purple-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">EDIT STATES<br />AVAILABLE</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Select US states allowed for sales</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-purple-600">
              {campaignSettings.states.map((stateCode: string) => CODE_TO_STATE_MAPPING[stateCode] || stateCode).length} states selected
            </p>
          </div>
        </div>
      </button>
    )}

    {loadingStates.zipcodes ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('zipcodes')}
        className="group bg-white border-2 border-indigo-200 rounded-2xl p-6 hover:border-indigo-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Target className="w-7 h-7 text-indigo-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">EDIT ZIPCODES<br />AVAILABLE</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Select zipcodes allowed for sales</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-indigo-600">
              {campaignSettings.allZipcodes ? 'All zipcodes' : `${campaignSettings.zipcodes.length} zipcodes selected`}
            </p>
          </div>
        </div>
      </button>
    )}

    {/* Age Eligibility Card */}
    {loadingStates.minAge || loadingStates.maxAge ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('ageEligibility')}
        className="group bg-white border-2 border-teal-200 rounded-2xl p-6 hover:border-teal-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Users className="w-7 h-7 text-teal-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">EDIT AGE<br />ELIGIBILITY</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Set minimum and maximum age requirements</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-teal-600">
              {campaignSettings.minAge && campaignSettings.maxAge 
                ? `Ages ${campaignSettings.minAge}-${campaignSettings.maxAge}` 
                : campaignSettings.minAge 
                  ? `Min age: ${campaignSettings.minAge}`
                  : campaignSettings.maxAge 
                    ? `Max age: ${campaignSettings.maxAge}`
                    : 'No age restrictions'}
            </p>
          </div>
        </div>
      </button>
    )}

    {/* Gender Eligibility Card */}
    {loadingStates.allowedGenders ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('genderEligibility')}
        className="group bg-white border-2 border-pink-200 rounded-2xl p-6 hover:border-pink-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Users className="w-7 h-7 text-pink-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">EDIT GENDER<br />ELIGIBILITY</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Select eligible gender categories</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-pink-600">
              {campaignSettings.allowedGenders.length > 0
                ? `${campaignSettings.allowedGenders.length} selected`
                : 'All genders allowed'}
            </p>
          </div>
        </div>
      </button>
    )}

    {/* Override Rate Limits Card */}
    {loadingStates.overrideRateLimits ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('overrideRateLimits')}
        className="group bg-white border-2 border-purple-200 rounded-2xl p-6 hover:border-purple-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <TrendingUp className="w-7 h-7 text-purple-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">OVERRIDE RATE<br />LIMITS</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Set min/max override commission rates</p>
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-purple-600">
              {campaignSettings.minOverrideRate != null && campaignSettings.maxOverrideRate != null
                ? `$${campaignSettings.minOverrideRate} - $${campaignSettings.maxOverrideRate}`
                : 'Using defaults'}
            </p>
          </div>
        </div>
      </button>
    )}

    {loadingStates.hiringFreeze ? (
      <ShimmerCard />
    ) : (
      <button
        onClick={() => openModal('hiringFreeze')}
        className="group bg-white border-2 border-orange-200 rounded-2xl p-6 hover:border-orange-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <Users className="w-7 h-7 text-orange-600" />
          </div>
          <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">HIRING FREEZE</h3>
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">Control new hire onboarding</p>
          <div className="pt-3 border-t border-gray-100">
            <p className={`text-xs font-semibold ${campaignSettings.hiringFreeze ? 'text-red-600' : 'text-green-600'}`}>
              {campaignSettings.hiringFreeze ? 'FREEZE ACTIVE' : 'HIRING OPEN'}
            </p>
          </div>
        </div>
      </button>
    )}

    {/* Insurance Carriers Card */}
    <button
      onClick={() => setShowInsuranceCarriersModal(true)}
      className="group bg-white border-2 border-cyan-200 rounded-2xl p-6 hover:border-cyan-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
    >
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
          <Building2 className="w-7 h-7 text-cyan-600" />
        </div>
        <h3 className="font-bold text-sm text-gray-900 mb-3 leading-tight">INSURANCE<br />CARRIERS</h3>
        <p className="text-xs text-gray-500 mb-3 leading-relaxed">Configure carrier availability & rates by state</p>
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-cyan-600">
            Click to configure
          </p>
        </div>
      </div>
    </button>
  </div>
</div>

        {/* Second Row - Campaign Status */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 border-b border-gray-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Campaign Status</h2>
              <p className="text-gray-600">Set campaign access and visibility levels for representatives</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {isUpdating ? (
              <ShimmerButton />
            ) : (
              <button
                onClick={() => updateCampaignStatus('ACTIVE')}
                className={`p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  campaign?.status === 'ACTIVE' 
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white ring-4 ring-green-300' 
                    : 'bg-gradient-to-br from-green-200 to-green-300 text-green-600 hover:from-green-300 hover:to-green-400'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Active</div>
                  <div className="text-xs opacity-90">Full access & functionality</div>
                </div>
              </button>
            )}

            {isUpdating ? (
              <ShimmerButton />
            ) : (
              <button
                onClick={() => updateCampaignStatus('INACTIVE')}
                className={`p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  campaign?.status === 'INACTIVE' 
                    ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-white ring-4 ring-yellow-300' 
                    : 'bg-gradient-to-br from-yellow-200 to-yellow-300 text-yellow-600 hover:from-yellow-300 hover:to-yellow-400'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Inactive</div>
                  <div className="text-xs opacity-90">New applications only. No Submissions Permitted.</div>
                </div>
              </button>
            )}

            {isUpdating ? (
              <ShimmerButton />
            ) : (
              <button
                onClick={() => updateCampaignStatus('DISABLED')}
                className={`p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  campaign?.status === 'DISABLED' 
                    ? 'bg-gradient-to-br from-red-500 to-red-600 text-white ring-4 ring-red-300' 
                    : 'bg-gradient-to-br from-red-200 to-red-300 text-red-600 hover:from-red-300 hover:to-red-400'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Disabled</div>
                  <div className="text-xs opacity-90">View only access</div>
                </div>
              </button>
            )}

            {isUpdating ? (
              <ShimmerButton />
            ) : (
              <button
                onClick={() => updateCampaignStatus('HIDDEN')}
                className={`p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  campaign?.status === 'HIDDEN' 
                    ? 'bg-gradient-to-br from-gray-500 to-gray-600 text-white ring-4 ring-gray-300' 
                    : 'bg-gradient-to-br from-gray-200 to-gray-300 text-gray-600 hover:from-gray-300 hover:to-gray-400'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">Hidden</div>
                  <div className="text-xs opacity-90">Not visible to reps</div>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Third Row - Campaign Sales Schedule */}
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-8 border-b border-gray-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Campaign Sales Schedule</h2>
              <p className="text-gray-600">Configure when representatives can write sales</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {loadingStates.salesDays ? (
              <ShimmerCard />
            ) : (
              <button
                onClick={() => openModal('salesDays')}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-lg transition-all duration-200 cursor-pointer"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-xl">DAYS OF WEEK ALLOWED TO WRITE SALES</h3>
                  <p className="text-sm text-gray-600">Select which days reps can perform sales activities</p>
                  <p className="text-xs text-purple-600 mt-2 font-medium">
                    {campaignSettings.salesDays.join(', ')}
                  </p>
                </div>
              </button>
            )}

            {loadingStates.salesTimes ? (
              <ShimmerCard />
            ) : (
              <button
                onClick={() => openModal('salesTimes')}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-lg transition-all duration-200 cursor-pointer"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-xl">TIMES OF DAY ALLOWED TO WRITE SALES</h3>
                  <p className="text-sm text-gray-600">Set specific time windows for sales activities</p>
                  <p className="text-xs text-purple-600 mt-2 font-medium">
                    {campaignSettings.salesTimes.is24Hours ? '24 hours a day' : `${campaignSettings.salesTimes.start} - ${campaignSettings.salesTimes.end}`}
                  </p>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Fourth Row - Campaign ORG Assignment */}
        {/* Fourth Row - Campaign ORG Assignment */}
<div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 sm:p-8">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
        <Users className="w-6 h-6 text-white" />
      </div>
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Campaign ORG Assignment</h2>
        <p className="text-sm text-gray-600 hidden sm:block">Drag and drop organizations between categories to manage access</p>
        <p className="text-sm text-gray-600 sm:hidden">Tap organizations to move between categories</p>
      </div>
    </div>
    
    {/* Organization Search */}
    <div className="relative w-full sm:w-64">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        placeholder="Search organizations..."
        value={orgSearchTerm}
        onChange={(e) => setOrgSearchTerm(e.target.value)}
        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
      />
    </div>
  </div>

  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
    {/* Assigned ORGs */}
    {/* Assigned ORGs */}
<div
  className={`bg-gradient-to-br from-green-50 to-green-100 border-2 rounded-xl p-4 sm:p-6 transition-all duration-200 ${
    dragOverTarget === 'assigned'
      ? 'border-green-400 shadow-lg lg:scale-105'
      : 'border-green-200'
  }`}
  onDragOver={(e) => {
    if (window.innerWidth >= 1024) handleDragOver(e, 'assigned')
  }}
  onDragLeave={() => {
    if (window.innerWidth >= 1024) handleDragLeave()
  }}
  onDrop={(e) => {
    if (window.innerWidth >= 1024) handleDrop(e, 'assigned')
  }}
>
  <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
      <h3 className="font-bold text-green-800 text-xs sm:text-sm lg:text-base truncate">ASSIGNED ORGS</h3>
    </div>
    <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
      {filterOrganizations(campaignSettings.assignedOrgs).length}
    </span>
  </div>
  <p className="text-xs sm:text-sm text-green-700 mb-3 sm:mb-4 line-clamp-2">Hired orgs permitted to write sales</p>
  <div className="space-y-2 sm:space-y-3 min-h-[120px] max-h-[300px] sm:max-h-[400px] overflow-y-auto pr-1">
    {isLoadingOrgs || isUpdatingOrgAssignment ? (
      <div className="space-y-2 sm:space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white border border-green-200 rounded-lg p-2 sm:p-3 animate-pulse">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-4 h-4 bg-gray-200 rounded flex-shrink-0"></div>
              <div className="h-4 bg-gray-200 rounded flex-1"></div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <>
        {filterOrganizations(campaignSettings.assignedOrgs).map((org, index) => (
          <div
            key={org.id || index}
            draggable={!isUpdatingOrgAssignment && window.innerWidth >= 1024}
            onDragStart={(e) => {
              if (window.innerWidth >= 1024) handleDragStart(e, org, 'assigned')
            }}
            onClick={() => {
              if (window.innerWidth < 1024 && !isUpdatingOrgAssignment) {
                const action = window.prompt(
                  `Move "${org.name}"?\n\nType:\n1 - Unassigned\n2 - Blacklisted\n\nOr press Cancel to keep here.`
                )
                if (action === '1') {
                  setDraggedItem({ org, source: 'assigned' })
                  handleDrop({ preventDefault: () => {} } as any, 'unassigned')
                } else if (action === '2') {
                  setDraggedItem({ org, source: 'assigned' })
                  handleDrop({ preventDefault: () => {} } as any, 'blacklisted')
                }
              }
            }}
            className={`bg-white border border-green-200 rounded-lg p-2 sm:p-3 flex items-center justify-between hover:border-green-300 transition-all duration-200 ${
              !isUpdatingOrgAssignment && window.innerWidth >= 1024 ? 'cursor-move' : ''
            } ${
              !isUpdatingOrgAssignment && window.innerWidth < 1024 ? 'cursor-pointer active:scale-95' : ''
            } ${
              isUpdatingOrgAssignment ? 'cursor-not-allowed opacity-50' : ''
            } ${
              draggedItem?.org === org ? 'opacity-50 scale-95' : 'hover:shadow-md'
            }`}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <GripVertical className="w-4 h-4 text-gray-400 hidden lg:block flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 text-xs sm:text-sm truncate">{org.name}</span>
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                    {org.type}
                  </span>
                </div>
              </div>
            </div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full flex-shrink-0 ml-2"></div>
          </div>
        ))}
        {filterOrganizations(campaignSettings.assignedOrgs).length === 0 && (
          <div className="text-center text-gray-400 py-6 sm:py-8 border-2 border-dashed border-gray-300 rounded-lg text-xs sm:text-sm">
            {orgSearchTerm ? 'No matches found' : 'No assigned organizations'}
          </div>
        )}
      </>
    )}
  </div>
</div>

{/* Unassigned ORGs */}
<div
  className={`bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 rounded-xl p-4 sm:p-6 transition-all duration-200 ${
    dragOverTarget === 'unassigned'
      ? 'border-yellow-400 shadow-lg lg:scale-105'
      : 'border-yellow-200'
  }`}
  onDragOver={(e) => {
    if (window.innerWidth >= 1024) handleDragOver(e, 'unassigned')
  }}
  onDragLeave={() => {
    if (window.innerWidth >= 1024) handleDragLeave()
  }}
  onDrop={(e) => {
    if (window.innerWidth >= 1024) handleDrop(e, 'unassigned')
  }}
>
  <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-xs sm:text-sm">?</span>
      </div>
      <h3 className="font-bold text-yellow-800 text-xs sm:text-sm lg:text-base truncate">UNASSIGNED</h3>
    </div>
    <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
      {filterOrganizations(campaignSettings.unassignedOrgs).length}
    </span>
  </div>
  <p className="text-xs sm:text-sm text-yellow-700 mb-3 sm:mb-4 line-clamp-2">Not hired, can't see campaign</p>
  <div className="space-y-2 sm:space-y-3 min-h-[120px] max-h-[300px] sm:max-h-[400px] overflow-y-auto pr-1">
    {isLoadingOrgs || isUpdatingOrgAssignment ? (
      <div className="space-y-2 sm:space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white border border-yellow-200 rounded-lg p-2 sm:p-3 animate-pulse">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-4 h-4 bg-gray-200 rounded flex-shrink-0"></div>
              <div className="h-4 bg-gray-200 rounded flex-1"></div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <>
        {filterOrganizations(campaignSettings.unassignedOrgs).map((org, index) => (
          <div
            key={org.id || index}
            draggable={!isUpdatingOrgAssignment && window.innerWidth >= 1024}
            onDragStart={(e) => {
              if (window.innerWidth >= 1024) handleDragStart(e, org, 'unassigned')
            }}
            onClick={() => {
              if (window.innerWidth < 1024 && !isUpdatingOrgAssignment) {
                const action = window.prompt(
                  `Move "${org.name}"?\n\nType:\n1 - Assigned\n2 - Blacklisted\n\nOr press Cancel to keep here.`
                )
                if (action === '1') {
                  setDraggedItem({ org, source: 'unassigned' })
                  handleDrop({ preventDefault: () => {} } as any, 'assigned')
                } else if (action === '2') {
                  setDraggedItem({ org, source: 'unassigned' })
                  handleDrop({ preventDefault: () => {} } as any, 'blacklisted')
                }
              }
            }}
            className={`bg-white border border-yellow-200 rounded-lg p-2 sm:p-3 flex items-center justify-between hover:border-yellow-300 transition-all duration-200 ${
              !isUpdatingOrgAssignment && window.innerWidth >= 1024 ? 'cursor-move' : ''
            } ${
              !isUpdatingOrgAssignment && window.innerWidth < 1024 ? 'cursor-pointer active:scale-95' : ''
            } ${
              isUpdatingOrgAssignment ? 'cursor-not-allowed opacity-50' : ''
            } ${
              draggedItem?.org === org ? 'opacity-50 scale-95' : 'hover:shadow-md'
            }`}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <GripVertical className="w-4 h-4 text-gray-400 hidden lg:block flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 text-xs sm:text-sm truncate">{org.name}</span>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                    {org.type}
                  </span>
                </div>
              </div>
            </div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-500 rounded-full flex-shrink-0 ml-2"></div>
          </div>
        ))}
        {filterOrganizations(campaignSettings.unassignedOrgs).length === 0 && (
          <div className="text-center text-gray-400 py-6 sm:py-8 border-2 border-dashed border-gray-300 rounded-lg text-xs sm:text-sm">
            {orgSearchTerm ? 'No matches found' : 'All orgs assigned or blacklisted'}
          </div>
        )}
      </>
    )}
  </div>
</div>

{/* Blacklisted ORGs */}
<div
  className={`bg-gradient-to-br from-red-50 to-red-100 border-2 rounded-xl p-4 sm:p-6 transition-all duration-200 ${
    dragOverTarget === 'blacklisted'
      ? 'border-red-400 shadow-lg lg:scale-105'
      : 'border-red-200'
  }`}
  onDragOver={(e) => {
    if (window.innerWidth >= 1024) handleDragOver(e, 'blacklisted')
  }}
  onDragLeave={() => {
    if (window.innerWidth >= 1024) handleDragLeave()
  }}
  onDrop={(e) => {
    if (window.innerWidth >= 1024) handleDrop(e, 'blacklisted')
  }}
>
  <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
        <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
      <h3 className="font-bold text-red-800 text-xs sm:text-sm lg:text-base truncate">BLACKLISTED</h3>
    </div>
    <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
      {filterOrganizations(campaignSettings.blacklistedOrgs).length}
    </span>
  </div>
  <p className="text-xs sm:text-sm text-red-700 mb-3 sm:mb-4 line-clamp-2">Blocked from campaign</p>
  <div className="space-y-2 sm:space-y-3 min-h-[120px] max-h-[300px] sm:max-h-[400px] overflow-y-auto pr-1">
    {isLoadingOrgs || isUpdatingOrgAssignment ? (
      <div className="space-y-2 sm:space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white border border-red-200 rounded-lg p-2 sm:p-3 animate-pulse">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-4 h-4 bg-gray-200 rounded flex-shrink-0"></div>
              <div className="h-4 bg-gray-200 rounded flex-1"></div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <>
        {filterOrganizations(campaignSettings.blacklistedOrgs).map((org, index) => (
          <div
            key={org.id || index}
            draggable={!isUpdatingOrgAssignment && window.innerWidth >= 1024}
            onDragStart={(e) => {
              if (window.innerWidth >= 1024) handleDragStart(e, org, 'blacklisted')
            }}
            onClick={() => {
              if (window.innerWidth < 1024 && !isUpdatingOrgAssignment) {
                const action = window.prompt(
                  `Move "${org.name}"?\n\nType:\n1 - Assigned\n2 - Unassigned\n\nOr press Cancel to keep here.`
                )
                if (action === '1') {
                  setDraggedItem({ org, source: 'blacklisted' })
                  handleDrop({ preventDefault: () => {} } as any, 'assigned')
                } else if (action === '2') {
                  setDraggedItem({ org, source: 'blacklisted' })
                  handleDrop({ preventDefault: () => {} } as any, 'unassigned')
                }
              }
            }}
            className={`bg-white border border-red-200 rounded-lg p-2 sm:p-3 flex items-center justify-between hover:border-red-300 transition-all duration-200 ${
              !isUpdatingOrgAssignment && window.innerWidth >= 1024 ? 'cursor-move' : ''
            } ${
              !isUpdatingOrgAssignment && window.innerWidth < 1024 ? 'cursor-pointer active:scale-95' : ''
            } ${
              isUpdatingOrgAssignment ? 'cursor-not-allowed opacity-50' : ''
            } ${
              draggedItem?.org === org ? 'opacity-50 scale-95' : 'hover:shadow-md'
            }`}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <GripVertical className="w-4 h-4 text-gray-400 hidden lg:block flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 text-xs sm:text-sm truncate">{org.name}</span>
                  <span className="text-xs bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                    {org.type}
                  </span>
                </div>
              </div>
            </div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full flex-shrink-0 ml-2"></div>
          </div>
        ))}
        {filterOrganizations(campaignSettings.blacklistedOrgs).length === 0 && (
          <div className="text-center text-gray-400 py-6 sm:py-8 border-2 border-dashed border-gray-300 rounded-lg text-xs sm:text-sm">
            {orgSearchTerm ? 'No matches found' : 'No blacklisted organizations'}
          </div>
        )}
      </>
    )}
  </div>
</div>

      {/* Modals for Edit Campaign Settings */}
      {/* Edit Display Name Modal */}
      {editModals.name && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Display Name</h3>
              <button onClick={() => closeModal('name')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Display Name</label>
                {loadingStates.name ? (
                  <ShimmerInput />
                ) : (
                  <input
                    type="text"
                    value={localName}
                    onChange={(e) => setLocalName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter campaign name"
                  />
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalName(campaignSettings.name)
                    closeModal('name')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await updateSetting('name', localName)
                    closeModal('name')
                  }}
                  disabled={loadingStates.name}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.name ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Countries Modal */}
      {editModals.countries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Select Countries</h3>
              <button onClick={() => closeModal('countries')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Select countries where sales are allowed for this campaign:</p>
              {loadingStates.countries ? (
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg animate-pulse">
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {Object.keys(COUNTRY_MAPPING).map(countryName => (
                    <label key={countryName} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={localCountries.includes(countryName)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setLocalCountries([...localCountries, countryName])
                          } else {
                            setLocalCountries(localCountries.filter(c => c !== countryName))
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{countryName}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    // Convert ISO codes back to country names for display
                    const countryNames = campaignSettings.countries.map((isoCode: string) => ISO_TO_COUNTRY_MAPPING[isoCode] || isoCode)
                    setLocalCountries(countryNames)
                    closeModal('countries')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Convert country names to ISO codes before sending to backend
                    const countryCodes = localCountries.map(countryName => COUNTRY_MAPPING[countryName])
                    await updateSetting('countries', countryCodes)
                    closeModal('countries')
                  }}
                  disabled={loadingStates.countries}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.countries ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit States Modal */}
      {editModals.states && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Select US States</h3>
            <button onClick={() => closeModal('states')} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 pb-2 border-b border-gray-100">
              <p className="text-sm text-gray-600 leading-relaxed">
                Select states where sales are allowed for this campaign
              </p>
              {!loadingStates.states && (
                <button
                  onClick={() => {
                    const allStates = Object.keys(STATE_MAPPING);
                    if (localStates.length === allStates.length) {
                      setLocalStates([]);
                    } else {
                      setLocalStates(allStates);
                    }
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-semibold whitespace-nowrap transition-colors"
                >
                  {localStates.length === Object.keys(STATE_MAPPING).length ? 'Clear All' : 'Select All'}
                </button>
              )}
            </div>
            {loadingStates.states ? (
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {[...Array(51)].map((_, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg animate-pulse">
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-2">
                {Object.keys(STATE_MAPPING).map(stateName => (
                  <label key={stateName} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={localStates.includes(stateName)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setLocalStates([...localStates, stateName])
                        } else {
                          setLocalStates(localStates.filter(s => s !== stateName))
                        }
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{stateName}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  // Convert state codes back to state names for display
                  const stateNames = campaignSettings.states.map((stateCode: string) => CODE_TO_STATE_MAPPING[stateCode] || stateCode)
                  setLocalStates(stateNames)
                  closeModal('states')
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Convert state names to state codes before sending to backend
                  const stateCodes = localStates.map(stateName => STATE_MAPPING[stateName])
                  await updateSetting('states', stateCodes)
                  closeModal('states')
                }}
                disabled={loadingStates.states}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingStates.states ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Edit Zipcodes Modal */}
      {editModals.zipcodes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Select Zipcodes</h3>
              <button onClick={() => closeModal('zipcodes')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-600 mb-4">Configure zipcode restrictions for this campaign:</p>
                
                {/* All Zipcodes Option */}
                <div className="mb-6">
                  <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-indigo-300 transition-all duration-200">
                    <input
                      type="checkbox"
                      checked={localAllZipcodes}
                      onChange={(e) => {
                        setLocalAllZipcodes(e.target.checked)
                        if (e.target.checked) {
                          setLocalZipcodes('')
                        }
                      }}
                      className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">Allow all zipcodes</div>
                      <div className="text-sm text-gray-600">No zipcode restrictions - all areas allowed</div>
                    </div>
                  </label>
                </div>

                {/* Specific Zipcodes Input */}
                {!localAllZipcodes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Zipcodes</label>
                    <p className="text-sm text-gray-600 mb-3">Enter zipcodes separated by commas (e.g., 90210, 10001, 33101):</p>
                    {loadingStates.zipcodes ? (
                      <ShimmerInput />
                    ) : (
                      <textarea
                        value={localZipcodes}
                        onChange={(e) => setLocalZipcodes(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-24 resize-none"
                        placeholder="Enter zipcodes separated by commas..."
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Current zipcodes: {localZipcodes ? localZipcodes.split(',').map(z => z.trim()).filter(z => z).length : 0} entered
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalZipcodes(campaignSettings.zipcodes.join(', '))
                    setLocalAllZipcodes(campaignSettings.allZipcodes)
                    closeModal('zipcodes')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const zipcodeArray = localAllZipcodes ? [] : localZipcodes.split(',').map(z => z.trim()).filter(z => z)
                    await updateSetting('zipcodes', { zipcodes: zipcodeArray, allZipcodes: localAllZipcodes })
                    closeModal('zipcodes')
                  }}
                  disabled={loadingStates.zipcodes}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.zipcodes ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hiring Freeze Modal */}
      {editModals.hiringFreeze && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Hiring Freeze Settings</h3>
              <button onClick={() => closeModal('hiringFreeze')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-orange-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Control New Hire Onboarding</h4>
                <p className="text-sm text-gray-600">Set whether new hires can be onboarded for this campaign</p>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-green-300 transition-all duration-200">
                  <input
                    type="radio"
                    name="hiringFreeze"
                    checked={!localHiringFreeze}
                    onChange={() => setLocalHiringFreeze(false)}
                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                  />
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <div>
                      <div className="font-semibold text-green-600">Hiring Open</div>
                      <div className="text-sm text-gray-600">Onboarding referral link active. Onboarding funnel active</div>
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-red-300 transition-all duration-200">
                  <input
                    type="radio"
                    name="hiringFreeze"
                    checked={localHiringFreeze}
                    onChange={() => setLocalHiringFreeze(true)}
                    className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                  />
                  <div>
                    <div className="font-semibold text-red-600">Hiring Freeze Active</div>
                    <div className="text-sm text-gray-600">Onboarding referral link inactive. New hires see freeze message</div>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalHiringFreeze(campaignSettings.hiringFreeze)
                    closeModal('hiringFreeze')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await updateSetting('hiringFreeze', localHiringFreeze)
                    closeModal('hiringFreeze')
                  }}
                  disabled={loadingStates.hiringFreeze}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.hiringFreeze ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Age Eligibility Modal */}
      {editModals.ageEligibility && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Age Eligibility Settings</h3>
              <button onClick={() => closeModal('ageEligibility')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-teal-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Set Age Requirements</h4>
                <p className="text-sm text-gray-600">Define minimum and maximum age for client eligibility (e.g., for Roblox: 19 and younger)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Age</label>
                  <input
                    type="number"
                    value={localMinAge || ''}
                    onChange={(e) => setLocalMinAge(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="e.g., 13"
                    min="0"
                    max="150"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no minimum</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Age</label>
                  <input
                    type="number"
                    value={localMaxAge || ''}
                    onChange={(e) => setLocalMaxAge(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="e.g., 19"
                    min="0"
                    max="150"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for no maximum</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Example:</strong> For Roblox campaign, set Min Age = 0 and Max Age = 19 to target clients aged 19 and younger.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalMinAge(campaignSettings.minAge)
                    setLocalMaxAge(campaignSettings.maxAge)
                    closeModal('ageEligibility')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await Promise.all([
                        updateSetting('minAge', localMinAge, true),
                        updateSetting('maxAge', localMaxAge, true)
                      ])
                      alert('Age eligibility updated successfully!')
                      closeModal('ageEligibility')
                    } catch (error) {
                      // Error alerts are already handled in updateSetting
                    }
                  }}
                  disabled={loadingStates.minAge || loadingStates.maxAge}
                  className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {(loadingStates.minAge || loadingStates.maxAge) ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Override Rate Limits Modal */}
      {editModals.overrideRateLimits && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Override Rate Limits</h3>
              <button onClick={() => closeModal('overrideRateLimits')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-purple-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Set Override Rate Limits</h4>
                <p className="text-sm text-gray-600">Define minimum and maximum override commission rates for this campaign</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Rate ($)</label>
                  <input
                    type="number"
                    value={localMinOverrideRate || ''}
                    onChange={(e) => setLocalMinOverrideRate(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 0.10"
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to use system default (${0.10})</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Rate ($)</label>
                  <input
                    type="number"
                    value={localMaxOverrideRate || ''}
                    onChange={(e) => setLocalMaxOverrideRate(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 100.00"
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to use system default (${10.00})</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> These limits will be enforced when creating or updating override rates for this campaign. Leave both empty to use system defaults.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalMinOverrideRate(campaignSettings.minOverrideRate)
                    setLocalMaxOverrideRate(campaignSettings.maxOverrideRate)
                    closeModal('overrideRateLimits')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await updateSetting('overrideRateLimits', {
                        minOverrideRate: localMinOverrideRate,
                        maxOverrideRate: localMaxOverrideRate
                      }, false)
                      alert('Override rate limits updated successfully!')
                      closeModal('overrideRateLimits')
                    } catch (error) {
                      // Error alerts are already handled in updateSetting
                    }
                  }}
                  disabled={loadingStates.overrideRateLimits}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.overrideRateLimits ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gender Eligibility Modal */}
      {editModals.genderEligibility && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Gender Eligibility</h3>
              <button onClick={() => closeModal('genderEligibility')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-pink-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Select Eligible Genders</h4>
                <p className="text-sm text-gray-600">Choose which gender categories are eligible for this campaign. Leave empty to allow all genders.</p>
              </div>

              <div className="space-y-3">
                {['MALE', 'FEMALE', 'ALL_GENDERS'].map(gender => (
                  <label key={gender} className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-pink-300 transition-all duration-200">
                    <input
                      type="checkbox"
                      checked={localAllowedGenders.includes(gender)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setLocalAllowedGenders([...localAllowedGenders, gender])
                        } else {
                          setLocalAllowedGenders(localAllowedGenders.filter(g => g !== gender))
                        }
                      }}
                      className="w-5 h-5 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">
                        {gender === 'ALL_GENDERS' ? 'All Genders' : gender.charAt(0) + gender.slice(1).toLowerCase()}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> If no genders are selected, all gender categories will be allowed by default.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalAllowedGenders(campaignSettings.allowedGenders)
                    closeModal('genderEligibility')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await updateSetting('allowedGenders', localAllowedGenders)
                    closeModal('genderEligibility')
                  }}
                  disabled={loadingStates.allowedGenders}
                  className="px-6 py-3 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.allowedGenders ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Days Modal */}
      {editModals.salesDays && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Sales Days Settings</h3>
              <button onClick={() => closeModal('salesDays')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Days of Week Allowed</label>
                <p className="text-sm text-gray-600 mb-4">Select which days representatives can perform sales activities:</p>
                {loadingStates.salesDays ? (
                  <div className="grid grid-cols-2 gap-2">
                    {[...Array(7)].map((_, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg animate-pulse">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={localSalesDays.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setLocalSalesDays([...localSalesDays, day])
                            } else {
                              setLocalSalesDays(localSalesDays.filter(d => d !== day))
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalSalesDays(campaignSettings.salesDays)
                    closeModal('salesDays')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await updateSetting('salesDays', localSalesDays)
                    closeModal('salesDays')
                  }}
                  disabled={loadingStates.salesDays}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.salesDays ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Times Modal */}
      {editModals.salesTimes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Sales Times Settings</h3>
              <button onClick={() => closeModal('salesTimes')} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-600 mb-4">Set the time window when representatives can perform sales activities:</p>
                
                {/* 24 Hours Checkbox */}
                <div className="mb-6">
                  <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-purple-300 transition-all duration-200">
                    <input
                      type="checkbox"
                      checked={localSalesTimes.is24Hours}
                      onChange={(e) => setLocalSalesTimes({...localSalesTimes, is24Hours: e.target.checked})}
                      className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">24 hours a day</div>
                      <div className="text-sm text-gray-600">Allow sales activities at any time</div>
                    </div>
                  </label>
                </div>

                {/* Time Selection - Only show when not 24 hours */}
                {!localSalesTimes.is24Hours && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                      {loadingStates.salesTimes ? (
                        <ShimmerInput />
                      ) : (
                        <input
                          type="time"
                          value={localSalesTimes.start}
                          onChange={(e) => setLocalSalesTimes({...localSalesTimes, start: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                      {loadingStates.salesTimes ? (
                        <ShimmerInput />
                      ) : (
                        <input
                          type="time"
                          value={localSalesTimes.end}
                          onChange={(e) => setLocalSalesTimes({...localSalesTimes, end: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setLocalSalesTimes(campaignSettings.salesTimes)
                    closeModal('salesTimes')
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await updateSetting('salesTimes', localSalesTimes)
                    closeModal('salesTimes')
                  }}
                  disabled={loadingStates.salesTimes}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingStates.salesTimes ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insurance Carriers Modal */}
      <InsuranceCarriersModal
        isOpen={showInsuranceCarriersModal}
        onClose={() => setShowInsuranceCarriersModal(false)}
        campaignId={campaignId || ""}
        campaignName={campaignSettings.name}
      />
      </div>
      </div>
    </div>
    </div>
  )
}
